/**
 * What the Add Outcomes modal hands back when an operator confirms (CAE-1553).
 *
 * A pick is a **catalogue outcome** the operator has chosen, carried in exactly
 * the shape the create endpoint takes — so the caller loops and posts, with
 * nothing to look up again. The modal is the only place holding the fixture,
 * market and outcome records together, so building the payload is its job.
 *
 * CONTEXT.md's line is kept on both sides of that handover: what is picked here
 * is an outcome, the feed's word for the thing a Reel Option is built from, and
 * what the caller creates from it is a Reel Option.
 */
import type { CreateReelOptionFromCataloguePayload } from '../../../../api/contests';
import type { CatalogueMarket, CatalogueOutcome } from '../../../../api/catalogue';
import type { ContestDetailFixture } from '../../../../api/contests';

export type OutcomePick = CreateReelOptionFromCataloguePayload;

/**
 * What the row calls a market or an outcome, in the operator's order of
 * preference — the drill-down's `marketLabel` rule, applied to both.
 *
 * The localised name is the feed's for this locale and the canonical is its
 * fallback. A market also carries an operator's own override, which wins; an
 * outcome has none until it is on a reel, where the override lives on the Reel
 * Option rather than on the catalogue row.
 */
export const marketPickLabel = (market: CatalogueMarket): string =>
  market.displayNameOverride ?? market.localisedName ?? market.name ?? market.type;

export const outcomePickLabel = (outcome: CatalogueOutcome): string =>
  outcome.localisedName ?? outcome.name;

/**
 * One picked outcome as the create endpoint wants it.
 *
 * `displayNameOverride` is always null: this modal picks, and renaming a Reel
 * Option is the reel table's own affordance (CAE-1552). Sending a name here
 * would be inventing one the operator never typed.
 *
 * The fixture is sent whole because the endpoint creates the `contest_fixtures`
 * row when it is missing. It cannot be here — every fixture the modal offers is
 * already attached — but the contract takes it, and sending the attached
 * fixture's own feed identity is what keeps the server's lookup a no-op rather
 * than a surprise.
 */
export const toOutcomePick = (
  fixture: ContestDetailFixture,
  market: CatalogueMarket,
  outcome: CatalogueOutcome,
): OutcomePick => ({
  fixture: {
    externalId: fixture.externalId,
    name: fixture.name,
    homeTeam: fixture.homeTeam,
    awayTeam: fixture.awayTeam,
    startTime: fixture.startTime,
    status: fixture.status,
  },
  externalMarketId: market.externalId,
  marketName: marketPickLabel(market),
  externalOutcomeId: outcome.externalId,
  outcomeName: outcomePickLabel(outcome),
  displayNameOverride: null,
});
